import type { KeyboardEvent } from "@react-types/shared";
import { Decimal } from "decimal.js";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { validateDecimalConstraints } from "../../utils/math/validateDecimalConstraints.ts";
import { VStack } from "../Stack/VStack.tsx";
import { TextField, type TextFieldProps } from "../TextField/TextField.tsx";

export type DecimalFieldProps = Omit<TextFieldProps, "onChange"> & {
  minValue?: number;
  maxValue?: number;
  maxDecimals?: number;
};

export function DecimalField({
  onInternalErrorChange,
  value,
  minValue,
  maxValue,
  maxDecimals,
  ...props
}: DecimalFieldProps & {
  onValueChange(value: number | null, decimal: Decimal | null): void;
  onInternalErrorChange?: (error: boolean) => void;
}): ReactNode {
  const [textValue, setTextValue] = useState(value ?? "");
  const isFocusedRef = useRef(false);
  const { decimalValue, decimalError } = useMemo(() => parseDecimalInput(textValue), [textValue]);
  const constraintErrorMessage = useMemo(
    () => validateDecimalConstraints(decimalValue, { minValue, maxValue, maxDecimals }),
    [decimalValue, maxDecimals, maxValue, minValue],
  );
  const currentErrorMessage = decimalError?.message ?? constraintErrorMessage;

  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }

    // Keep the raw user input stable while editing. Parent consumers often round-trip
    // through `number` and immediately stringify the value again, which rewrites exponent
    // notation (for example `1e-6` -> `0.000001`) and causes the caret to jump.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTextValue(value ?? "");
  }, [value]);

  // make the outside aware of the current error state
  useEffect(() => {
    if (onInternalErrorChange) {
      onInternalErrorChange(currentErrorMessage != null);
    }
  }, [currentErrorMessage, onInternalErrorChange]);

  useEffect(() => {
    props.onValueChange(decimalValue?.toNumber() ?? null, decimalValue);
    // props.onValueChange is not stable
    // eslint-disable-next-line
  }, [decimalValue]);

  const handleKeyDown = (e: KeyboardEvent) => {
    props.onKeyDown?.(e);

    const isCtrlKey = e.ctrlKey || e.metaKey;
    if (isCtrlKey && (e.key === "c" || e.key === "v" || e.key === "x" || e.key === "a")) {
      return; // Allow Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A
    }

    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "-",
      ".",
      "e",
      "+",
    ];

    if (!allowedKeys.includes(e.key)) {
      e.preventDefault();
    }

    const value = (e.currentTarget as HTMLInputElement).value;

    // Prevent multiple occurrences of 'e', '.'
    if ((e.key === "e" && value.includes("e")) || (e.key === "." && value.includes("."))) {
      e.preventDefault();
    }

    // Prevent '-' and '+' unless they are at the start or immediately after 'e' or 'E'
    const cursorPos = e.currentTarget.selectionStart;
    if ((e.key === "-" || e.key === "+") && cursorPos !== 0 && value[cursorPos - 1] !== "e") {
      e.preventDefault();
    }
  };

  return (
    <VStack>
      <TextField
        {...props}
        value={textValue}
        onChange={(value) => {
          setTextValue(value.replaceAll(",", "")); // `,` are not allowed, but we replace it just in case of pasting en-US formatted value
        }}
        onFocus={(event) => {
          isFocusedRef.current = true;
          props.onFocus?.(event);
        }}
        onBlur={(event) => {
          isFocusedRef.current = false;
          props.onBlur?.(event);
        }}
        errorMessage={
          (typeof props.errorMessage === "string" ? props.errorMessage : "") +
          (props.errorMessage && currentErrorMessage ? ", " : "") +
          (currentErrorMessage ?? "")
        }
        isInvalid={props.isInvalid || currentErrorMessage != null}
        onKeyDown={handleKeyDown}
      />
    </VStack>
  );
}

function parseDecimalInput(rawValue: string): {
  decimalValue: Decimal | null;
  decimalError: Error | null;
} {
  const normalizedValue = rawValue.trim();
  if (normalizedValue === "") {
    return { decimalValue: null, decimalError: null };
  }
  try {
    const decimal = new Decimal(normalizedValue);
    const parsedDecimal = new Decimal(decimal.toNumber());
    if (Number.isNaN(parsedDecimal.toNumber())) {
      throw new DecimalFieldError("Invalid Number");
    }
    if (!Number.isFinite(parsedDecimal.toNumber())) {
      throw new DecimalFieldError("Invalid Number: Infinity");
    }
    if (!decimal.equals(parsedDecimal)) {
      throw new DecimalFieldError("Too many digits");
    }
    return { decimalValue: decimal, decimalError: null };
  } catch (error) {
    return {
      decimalValue: null,
      decimalError: error instanceof DecimalFieldError ? error : new Error("Invalid number"),
    };
  }
}

class DecimalFieldError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DecimalFieldError";
  }
}
